import type { ReactNode } from 'react';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FEHLERZUSTAND (Auftrag 17.09.2026, Abschnitt 21)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Technische Fehler nicht ungefiltert an den Benutzer ausgeben.
 *    Benutzerfreundliche Meldung mit [Erneut versuchen] und aufklappbaren
 *    technischen Details."
 *
 * Der Bestand zeigt heute an vielen Stellen `e.message` roh an - also den
 * HTTP-Text oder eine EF-Ausnahme. Das ist für den Fachplaner wertlos und
 * für den Entwickler unentbehrlich. Beides geht: oben der Satz, unten
 * zugeklappt der Wortlaut.
 *
 * `technisch` nimmt alles entgegen, was im `catch` ankommt (Error, String,
 * Response-Text) - `technischerText` macht daraus Text, ohne selbst zu
 * scheitern.
 */

export interface ErrorStateProps {
  /** Überschrift in Alltagssprache. Vorgabe: „Die Daten konnten nicht geladen werden." */
  titel?: string;
  /** Was das für den Anwender bedeutet und was er tun kann. */
  meldung?: ReactNode;
  /** Der rohe Fehler - wird NUR aufgeklappt gezeigt. */
  technisch?: unknown;
  /** Zeigt `[Erneut versuchen]`, wenn gesetzt. */
  beiErneutVersuchen?: () => void;
  /** Weitere Knöpfe rechts neben dem Wiederholknopf. */
  aktionen?: ReactNode;
  klein?: boolean;
  className?: string;
}

/** Aus einem beliebigen Fangwert lesbaren Text machen. */
export function technischerText(fehler: unknown): string {
  if (fehler === null || fehler === undefined) return '';
  if (typeof fehler === 'string') return fehler;
  if (fehler instanceof Error) {
    return fehler.stack && fehler.stack.includes(fehler.message)
      ? fehler.stack
      : `${fehler.name}: ${fehler.message}`;
  }
  try {
    return JSON.stringify(fehler, null, 2);
  } catch {
    return String(fehler);
  }
}

export function ErrorState({
  titel = 'Die Daten konnten nicht geladen werden.',
  meldung,
  technisch,
  beiErneutVersuchen,
  aktionen,
  klein,
  className,
}: ErrorStateProps) {
  const roh = technischerText(technisch);
  const klassen = ['m-fehlerzustand'];
  if (klein) klassen.push('m-fehlerzustand-klein');
  if (className) klassen.push(className);

  return (
    <div className={klassen.join(' ')} role="alert">
      <div className="m-fehlerzustand-kopf">
        <span className="m-fehlerzustand-zeichen" aria-hidden="true">×</span>
        <div>
          <p className="m-fehlerzustand-titel">{titel}</p>
          {meldung && <div className="m-fehlerzustand-text">{meldung}</div>}
        </div>
      </div>

      {(beiErneutVersuchen || aktionen) && (
        <div className="m-fehlerzustand-aktion">
          {beiErneutVersuchen && (
            <button type="button" className="m-knopf" onClick={beiErneutVersuchen}>
              Erneut versuchen
            </button>
          )}
          {aktionen}
        </div>
      )}

      {roh !== '' && (
        <details className="m-fehlerzustand-details">
          <summary>Technische Einzelheiten</summary>
          <pre>{roh}</pre>
        </details>
      )}
    </div>
  );
}
